import React from "react";

import CryptoJS from "crypto-js";
import Config from "../config";


export const stableStringify = (value: any): string => {
  if (value === null || typeof value !== "object") {
    if (value instanceof Date) return JSON.stringify(value.toISOString());
    if (value === undefined) return "null";
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return "[" + value.map(stableStringify).join(",") + "]";
  }

  const keys = Object.keys(value).sort();
  const keyValuePairs = keys.map(
    (key) => JSON.stringify(key) + ":" + stableStringify(value[key])
  );
  return "{" + keyValuePairs.join(",") + "}";
};




const signData = (data): string => {

  const HMAC_KEY = Config.SECURE_HMAC_SECRET_KEY;

  const normalized = stableStringify(data);
  const hash = CryptoJS.HmacSHA256(normalized, HMAC_KEY);
  const signature = CryptoJS.enc.Base64.stringify(hash);

  return signature;
}


const verifySignature = (data, providedSignature: string): boolean => {

  const HMAC_KEY = Config.SECURE_HMAC_SECRET_KEY;

  const normalized = stableStringify(data);
  const hash = CryptoJS.HmacSHA256(normalized, HMAC_KEY);
  const expectedSign = CryptoJS.enc.Base64.stringify(hash);
  return providedSignature === expectedSign;
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  return Uint8Array.from(binary, c => c.charCodeAt(0));
}

async function importKey(secretKey) {
  return await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secretKey),
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"] // ✅ allow both
  );
}

export async function decryptString(secretKey, encryptedBase64) {
  const encryptedBytes = base64ToBytes(encryptedBase64);

  // AES-GCM nonce size = 12 bytes
  const nonce = encryptedBytes.slice(0, 12);
  const ciphertext = encryptedBytes.slice(12);

  const key = await importKey(secretKey);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: nonce,
    },
    key,
    ciphertext
  );

  return new TextDecoder().decode(decrypted);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

export async function encryptString(secretKey, plainText) {
  const key = await importKey(secretKey);

  const encoder = new TextEncoder();
  const data = encoder.encode(plainText);

  // AES-GCM requires 12 byte IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    data
  );

  const encryptedBytes = new Uint8Array(encryptedBuffer);

  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + encryptedBytes.length);
  combined.set(iv);
  combined.set(encryptedBytes, iv.length);

  return bytesToBase64(combined);
}

export const HmacSignatureUtilities = {
  signData,
  verifySignature
}
